import type { GeometryType, Stone } from '../domain/types';
import { Stone3DViewer } from './Stone3DViewer';

interface StoneIconProps {
  stone: Stone;
  stage: number;
  size?: number;
  autoRotate?: boolean;
}

/** 石のアイコンを描画。
 *  modelUrl が指定されていれば3Dレンダラを試み、失敗時はSVG幾何学にフォールバック。
 *  ここでは3Dが読み込めたかどうかの判定は内部で行い、表示は3D or SVGを出し分け。 */
export function StoneIcon({ stone, stage, size = 120, autoRotate = true }: StoneIconProps) {
  // modelUrlがある場合は3D表示を試みる
  if (stone.modelUrl) {
    return (
      <Stone3DViewer
        stone={stone}
        stage={stage}
        size={size}
        autoRotate={autoRotate}
      />
    );
  }

  // なければSVG幾何学
  return <StoneSvg stone={stone} stage={stage} size={size} />;
}

interface StoneSvgProps {
  stone: Stone;
  stage: number;
  size: number;
}

function StoneSvg({ stone, stage, size }: StoneSvgProps) {
  const { type, baseColor, glowColor } = stone.geometryParams;
  const id = `g-${stone.id}-${stage}`;

  // 段階に応じたglowの強さ
  const glowOpacity = Math.min(0.2 + stage * 0.2, 0.95);
  const glowRadius = 6 + stage * 4;
  const filterId = `glow-${stone.id}-${stage}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label={`${stone.name} 段階${stage}`}
    >
      <defs>
        <radialGradient id={id} cx="40%" cy="35%" r="70%">
          <stop offset="0%" stopColor={glowColor} stopOpacity="0.95" />
          <stop offset="60%" stopColor={baseColor} stopOpacity="0.95" />
          <stop offset="100%" stopColor={baseColor} stopOpacity="0.55" />
        </radialGradient>
        <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation={glowRadius / 4} result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g filter={`url(#${filterId})`} style={{ opacity: glowOpacity }}>
        <GeometryShape type={type} gradientId={id} />
      </g>
    </svg>
  );
}

function GeometryShape({ type, gradientId }: { type: GeometryType; gradientId: string }) {
  switch (type) {
    case 'sphere':
      return <circle cx="50" cy="50" r="34" fill={`url(#${gradientId})`} />;
    case 'octahedron':
      return (
        <g fill={`url(#${gradientId})`} stroke={gradientId} strokeWidth="0.5">
          <polygon points="50,12 78,50 50,88 22,50" />
          <polygon points="22,50 50,50 78,50 50,50" opacity="0.3" />
        </g>
      );
    case 'dodecahedron':
      return (
        <g fill={`url(#${gradientId})`}>
          <polygon points="50,10 78,30 78,70 50,90 22,70 22,30" />
          <polygon
            points="50,30 65,42 65,58 50,70 35,58 35,42"
            fill="#000"
            opacity="0.18"
          />
        </g>
      );
  }
}
